//
// Vacation Settings API service
// =============================
//
// PURPOSE
// -------
// This module provides the frontend API layer for:
//
// - leave profile data
// - work schedule data
// - leave types dictionary used by HR / leave-request UI
// - yearly leave balances and balance adjustments
//
// CURRENT APP ARCHITECTURE ALIGNMENT
// ----------------------------------
// 1) This module is intentionally THIN.
//    - It should not contain UI logic.
//    - It should not perform auth redirects.
//    - It should not call softLogout() or handleLogout().
//
// 2) Authentication / session handling is centralized elsewhere.
//    - apiClient owns token refresh and session header behavior.
//    - pages/components react to auth failures (401 / refresh failure / session ambiguity).
//
// 3) This service focuses on:
//    - calling backend endpoints
//    - supporting AbortSignal for GET-like page loads
//    - normalizing backend payloads into predictable frontend-friendly shapes
//
// BACKEND ROUTES (current contract)
// ---------------------------------
//   GET  /vacation-settings/:userId
//   PUT  /vacation-settings/:userId/profile
//   PUT  /vacation-settings/:userId/work-schedule
//   PUT  /vacation-settings/:userId/work-pattern-assignment
//   DELETE /vacation-settings/:userId/work-pattern-assignment
//   GET  /vacation-settings/:userId/balances?year=YYYY&includePending=1|0
//   PUT  /vacation-settings/:userId/balance-adjustments?year=YYYY&includePending=1|0
//
// DESIGN PRINCIPLES
// -----------------
// - Keep the module stable and predictable.
// - Keep the normalization logic here so UI components do not need to know
//   all historical backend response variants.
// - Keep errors transparent: log real failures, but never treat request cancellation
//   as a "real error".
//

import apiClient from "../utils/apiClient";
import { isRequestCanceled, assertValidId } from "./httpUtils";

/* -------------------------------------------------------------------------- */
/* Small normalization helpers                                                */
/* -------------------------------------------------------------------------- */

/**
 * Convert any value into a finite number, otherwise fallback.
 *
 * Useful because some backend fields may arrive as strings.
 */
function toNumberOr(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Normalize an integer-like day-of-week value.
 *
 * Expected domain in this app:
 * - 1..7
 *
 * If invalid, return null so the caller can filter it out.
 */
function normalizeDayOfWeek(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return null;
  if (n < 1 || n > 7) return null;
  return n;
}

/**
 * Normalize a possibly-null string field.
 *
 * Returns:
 * - trimmed string when meaningful
 * - null otherwise
 */
function normalizeNullableString(value) {
  const s = String(value ?? "").trim();
  return s || null;
}

/**
 * Safely ensure an array shape.
 */
function asArray(value) {
  return Array.isArray(value) ? value : [];
}

/* -------------------------------------------------------------------------- */
/* Work schedule normalization                                                */
/* -------------------------------------------------------------------------- */

/**
 * Normalize a single work-schedule day entry.
 *
 * Supported input variants:
 * - { day_of_week, minutes }
 * - { dayOfWeek, minutes }
 *
 * Output:
 * - { day_of_week, minutes }
 * - or null if invalid
 */
function normalizeWorkScheduleDay(row) {
  if (!row || typeof row !== "object") return null;

  const dayOfWeek = normalizeDayOfWeek(row.day_of_week ?? row.dayOfWeek);
  if (dayOfWeek == null) return null;

  return {
    day_of_week: dayOfWeek,
    minutes: toNumberOr(row.minutes, 0),
  };
}

/**
 * Normalize a structured work schedule object.
 *
 * Expected structured shape:
 * {
 *   effective_from,
 *   effective_to,
 *   days: [{ day_of_week, minutes }, ...]
 * }
 *
 * Output is always:
 * {
 *   effective_from: string|null,
 *   effective_to: string|null,
 *   days: [...]
 * }
 */
function normalizeWorkScheduleOverride(row) {
  if (!row || typeof row !== "object") return null;

  const workDate = normalizeNullableString(row.work_date ?? row.workDate);
  if (!workDate) return null;

  return {
    id: row.id,
    user_id: row.user_id ?? row.userId ?? null,
    work_date: workDate,
    minutes: toNumberOr(row.minutes, 0),
    reason: normalizeNullableString(row.reason) || "",
    active: row.active !== false,
    createdAt: row.createdAt ?? row.created_at ?? null,
    updatedAt: row.updatedAt ?? row.updated_at ?? null,
  };
}

function normalizeWorkScheduleOverrides(value) {
  return asArray(value)
    .map(normalizeWorkScheduleOverride)
    .filter(Boolean)
    .sort((a, b) => String(b.work_date).localeCompare(String(a.work_date)));
}

function normalizeStructuredWorkSchedule(schedule) {
  if (!schedule || typeof schedule !== "object") {
    return {
      effective_from: null,
      effective_to: null,
      days: [],
    };
  }

  const days = asArray(schedule.days)
    .map(normalizeWorkScheduleDay)
    .filter(Boolean)
    .sort((a, b) => a.day_of_week - b.day_of_week);

  return {
    effective_from: normalizeNullableString(
      schedule.effective_from ?? schedule.effectiveFrom
    ),
    effective_to: normalizeNullableString(
      schedule.effective_to ?? schedule.effectiveTo
    ),
    days,
  };
}

/**
 * Extract and normalize the newest effective schedule from "rows history" format.
 *
 * Expected row-history style:
 * [
 *   { effective_from, effective_to, day_of_week, minutes },
 *   ...
 * ]
 *
 * Strategy:
 * - pick the newest effective_from
 * - group rows for that effective_from
 * - convert them into structured schedule format
 *
 * Why this exists:
 * - some backend endpoints may return schedule history rows instead of a pre-grouped object
 * - the UI usually wants only the currently effective schedule block
 */
function normalizeWorkScheduleFromRows(scheduleRows) {
  const rows = asArray(scheduleRows);
  if (!rows.length) {
    return {
      effective_from: null,
      effective_to: null,
      days: [],
    };
  }

  const normalizedRows = rows
    .map((row) => ({
      effective_from: normalizeNullableString(
        row?.effective_from ?? row?.effectiveFrom
      ),
      effective_to: normalizeNullableString(
        row?.effective_to ?? row?.effectiveTo
      ),
      day_of_week: normalizeDayOfWeek(row?.day_of_week ?? row?.dayOfWeek),
      minutes: toNumberOr(row?.minutes, 0),
    }))
    .filter((row) => row.effective_from && row.day_of_week != null);

  if (!normalizedRows.length) {
    return {
      effective_from: null,
      effective_to: null,
      days: [],
    };
  }

  /**
   * YYYY-MM-DD string comparison is safe lexicographically for max/min purposes.
   */
  const newestFrom = normalizedRows
    .map((row) => row.effective_from)
    .sort((a, b) => b.localeCompare(a))[0];

  const rowsForNewest = normalizedRows
    .filter((row) => row.effective_from === newestFrom)
    .sort((a, b) => a.day_of_week - b.day_of_week);

  return {
    effective_from: newestFrom,
    effective_to: rowsForNewest[0]?.effective_to ?? null,
    days: rowsForNewest.map((row) => ({
      day_of_week: row.day_of_week,
      minutes: row.minutes,
    })),
  };
}

/* -------------------------------------------------------------------------- */
/* Leave types / balances normalization                                       */
/* -------------------------------------------------------------------------- */

/**
 * Normalize leave types list.
 *
 * We keep the objects mostly as backend provides them,
 * because UI often needs many fields from master data.
 *
 * We only ensure:
 * - always an array
 * - stable ordering is left to UI if needed
 */
function normalizeLeaveTypes(data) {
  return asArray(
    data?.leaveTypes ??
      data?.leave_types ??
      data?.types ??
      []
  );
}

/**
 * Normalize balances list from different possible backend shapes.
 *
 * Supported:
 * - { balances: [...] }
 * - { balances: { balances: [...] } }
 * - { rows: [...] }   (for balances-specific endpoint we handle separately)
 */
function normalizeBundleBalances(data) {
  if (Array.isArray(data?.balances)) return data.balances;
  if (Array.isArray(data?.balances?.balances)) return data.balances.balances;
  return [];
}

/* -------------------------------------------------------------------------- */
/* Bundle normalization                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Normalize the main /vacation-settings/:userId bundle into a stable shape.
 *
 * Returned shape:
 * {
 *   profile,
 *   workSchedule: {
 *     effective_from,
 *     effective_to,
 *     days: [{ day_of_week, minutes }]
 *   },
 *   leaveTypes: [],
 *   balances: []
 * }
 *
 * Why this matters:
 * - frontend pages/components should not need to understand every legacy backend variant
 * - it keeps the rest of the UI simpler and less fragile
 */
function normalizeVacationSettingsBundle(data) {
  const payload = data && typeof data === "object" ? data : {};

  const profile =
    payload.profile ||
    payload.leaveProfile ||
    payload.userLeaveProfile ||
    null;

  let workSchedule = {
    effective_from: null,
    effective_to: null,
    days: [],
  };

  /**
   * Support both:
   * - structured schedule: payload.workSchedule / payload.schedule
   * - row history: payload.schedules
   */
  const structuredSchedule = payload.workSchedule || payload.schedule || null;

  if (structuredSchedule && Array.isArray(structuredSchedule.days)) {
    workSchedule = normalizeStructuredWorkSchedule(structuredSchedule);
  } else if (Array.isArray(payload.schedules) && payload.schedules.length > 0) {
    workSchedule = normalizeWorkScheduleFromRows(payload.schedules);
  }

  return {
    profile,
    workSchedule,
    workScheduleOverrides: normalizeWorkScheduleOverrides(
      payload.workScheduleOverrides ||
        payload.work_schedule_overrides ||
        payload.scheduleOverrides ||
        payload.schedule_overrides
    ),

    // Reusable work-pattern data introduced for rotating schedules.
    // Keep these fields in the normalized bundle so VacationSettingsSection
    // can assign/clear work patterns without knowing backend response variants.
    workPatternAssignment:
      payload.workPatternAssignment ||
      payload.work_pattern_assignment ||
      null,
    workPatterns: asArray(payload.workPatterns || payload.work_patterns),

    leaveTypes: normalizeLeaveTypes(payload),
    balances: normalizeBundleBalances(payload),
  };
}

/* -------------------------------------------------------------------------- */
/* Shared request helpers                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Log only real failures.
 * Request cancellation is expected control flow in React apps.
 */
function logRealFailure(scope, error) {
  if (isRequestCanceled(error)) return;
  // eslint-disable-next-line no-console
  console.error(`[vacationSettingsService] ❌ ${scope} failed:`, error);
}

/* -------------------------------------------------------------------------- */
/* Bundle endpoint                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Fetch the complete vacation-settings bundle for one user.
 *
 * Endpoint:
 *   GET /vacation-settings/:userId
 *
 * Common usage:
 * - leave request modal
 * - HR settings/profile pages
 * - leave configuration UIs
 *
 * @param {number|string} userId
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{
 *   profile: any,
 *   workSchedule: { effective_from: string|null, effective_to: string|null, days: Array },
 *   leaveTypes: Array,
 *   balances: Array
 * }>}
 */
export async function fetchVacationSettings(userId, options = {}) {
  try {
    assertValidId(userId, "userId");

    const res = await apiClient.get(`/vacation-settings/${userId}`, {
      withCredentials: true,
      signal: options.signal,
    });

    return normalizeVacationSettingsBundle(res.data);
  } catch (error) {
    logRealFailure("fetchVacationSettings", error);
    throw error;
  }
}

/* -------------------------------------------------------------------------- */
/* Profile                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Upsert or update the leave profile for a user.
 *
 * Endpoint:
 *   PUT /vacation-settings/:userId/profile
 *
 * We return the normalized bundle because backend may respond
 * with a refreshed full settings object.
 *
 * @param {number|string} userId
 * @param {object} profilePayload
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object>}
 */
export async function upsertLeaveProfile(userId, profilePayload, options = {}) {
  try {
    assertValidId(userId, "userId");

    const res = await apiClient.put(
      `/vacation-settings/${userId}/profile`,
      profilePayload,
      {
        withCredentials: true,
        signal: options.signal,
      }
    );

    return normalizeVacationSettingsBundle(res.data);
  } catch (error) {
    logRealFailure("upsertLeaveProfile", error);
    throw error;
  }
}

/* -------------------------------------------------------------------------- */
/* Work schedule                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Save / replace the work schedule for a user.
 *
 * Endpoint:
 *   PUT /vacation-settings/:userId/work-schedule
 *
 * We return the normalized bundle because backend may respond
 * with the refreshed settings state.
 *
 * @param {number|string} userId
 * @param {object} schedulePayload
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object>}
 */
export async function setWorkSchedule(userId, schedulePayload, options = {}) {
  try {
    assertValidId(userId, "userId");

    const res = await apiClient.put(
      `/vacation-settings/${userId}/work-schedule`,
      schedulePayload,
      {
        withCredentials: true,
        signal: options.signal,
      }
    );

    return normalizeVacationSettingsBundle(res.data);
  } catch (error) {
    logRealFailure("setWorkSchedule", error);
    throw error;
  }
}


/* -------------------------------------------------------------------------- */
/* Work pattern assignment                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Assign a reusable rotating work pattern to a user.
 *
 * Endpoint:
 *   PUT /vacation-settings/:userId/work-pattern-assignment
 *
 * The backend deactivates the previous active assignment and creates a new
 * auditable assignment row. Future leave calculations then resolve concrete
 * dates from this pattern unless an exact schedule or one-day override wins.
 *
 * @param {number|string} userId
 * @param {object} payload
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object>}
 */
export async function setWorkPatternAssignment(userId, payload, options = {}) {
  try {
    assertValidId(userId, "userId");

    const res = await apiClient.put(
      `/vacation-settings/${userId}/work-pattern-assignment`,
      payload,
      {
        withCredentials: true,
        signal: options.signal,
      }
    );

    return normalizeVacationSettingsBundle(res.data);
  } catch (error) {
    logRealFailure("setWorkPatternAssignment", error);
    throw error;
  }
}

/**
 * Clear/deactivate the current work-pattern assignment for a user.
 *
 * Endpoint:
 *   DELETE /vacation-settings/:userId/work-pattern-assignment
 *
 * @param {number|string} userId
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object>}
 */
export async function clearWorkPatternAssignment(userId, options = {}) {
  try {
    assertValidId(userId, "userId");

    const res = await apiClient.delete(
      `/vacation-settings/${userId}/work-pattern-assignment`,
      {
        withCredentials: true,
        signal: options.signal,
      }
    );

    return normalizeVacationSettingsBundle(res.data);
  } catch (error) {
    logRealFailure("clearWorkPatternAssignment", error);
    throw error;
  }
}

/* -------------------------------------------------------------------------- */
/* Balances                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Fetch computed leave balances for a given year.
 *
 * Endpoint:
 *   GET /vacation-settings/:userId/balances?year=YYYY&includePending=1|0
 *
 * Expected response shape:
 *   {
 *     year,
 *     includePending,
 *     rows: [...]
 *   }
 *
 * For backward compatibility with your current frontend usage,
 * this function returns ONLY the rows array.
 *
 * If later you need richer metadata in UI, you can add a parallel helper like:
 *   fetchLeaveBalancesBundle(...)
 *
 * @param {number|string} userId
 * @param {number|string} year
 * @param {object} [options]
 * @param {boolean} [options.includePending=false]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<Array>}
 */
export async function fetchLeaveBalances(userId, year, options = {}) {
  try {
    assertValidId(userId, "userId");

    const res = await apiClient.get(`/vacation-settings/${userId}/balances`, {
      withCredentials: true,
      signal: options.signal,
      params: {
        year,
        includePending: options.includePending ? 1 : 0,
      },
    });

    const data = res.data;
    return Array.isArray(data?.rows) ? data.rows : [];
  } catch (error) {
    logRealFailure("fetchLeaveBalances", error);
    throw error;
  }
}

/**
 * Save leave balance adjustments.
 *
 * Endpoint:
 *   PUT /vacation-settings/:userId/balance-adjustments?year=YYYY&includePending=1|0
 *
 * Body:
 *   { adjustments: [...] }
 *
 * Expected response:
 *   {
 *     year,
 *     includePending,
 *     rows: [...]
 *   }
 *
 * For compatibility, this function returns rows only.
 *
 * @param {number|string} userId
 * @param {number|string} year
 * @param {Array} adjustments
 * @param {object} [options]
 * @param {boolean} [options.includePending=false]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<Array>}
 */
export async function saveLeaveBalanceAdjustments(
  userId,
  year,
  adjustments,
  options = {}
) {
  try {
    assertValidId(userId, "userId");

    const res = await apiClient.put(
      `/vacation-settings/${userId}/balance-adjustments`,
      { adjustments },
      {
        withCredentials: true,
        signal: options.signal,
        params: {
          year,
          includePending: options.includePending ? 1 : 0,
        },
      }
    );

    const data = res.data;
    return Array.isArray(data?.rows) ? data.rows : [];
  } catch (error) {
    logRealFailure("saveLeaveBalanceAdjustments", error);
    throw error;
  }
}

/**
 * Reset and rebuild annual Leave/HR summaries for one user/year.
 *
 * Endpoint:
 *   DELETE /vacation-settings/:userId/year-summaries?year=YYYY&includePending=1|0
 *
 * This administrative action removes generated annual summary rows and manual
 * yearly adjustments for the selected user/year, then returns freshly computed
 * rows based on current Leave Type policies, Leave Requests and user profile.
 *
 * It intentionally does NOT delete Leave Requests or schedule/profile master
 * data.  Use this when HR wants to recreate the annual summary after changing
 * Leave Type policy matrix configuration during rollout.
 *
 * @param {number|string} userId
 * @param {number|string} year
 * @param {object} [options]
 * @param {boolean} [options.includePending=false]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<Array>}
 */
export async function resetLeaveYearSummaries(userId, year, options = {}) {
  try {
    assertValidId(userId, "userId");

    const res = await apiClient.delete(
      `/vacation-settings/${userId}/year-summaries`,
      {
        withCredentials: true,
        signal: options.signal,
        params: {
          year,
          includePending: options.includePending ? 1 : 0,
        },
      }
    );

    const data = res.data;
    return Array.isArray(data?.rows) ? data.rows : [];
  } catch (error) {
    logRealFailure("resetLeaveYearSummaries", error);
    throw error;
  }
}


/**
 * Save or update one employee-specific daily schedule exception.
 *
 * The backend upserts by (user_id, work_date).  This is intentionally not a
 * weekly schedule editor: it is a single-day override that has higher leave
 * duration priority than exact weekly schedules and rotating patterns.
 */
export async function setWorkScheduleOverride(userId, overridePayload, options = {}) {
  try {
    assertValidId(userId, "userId");

    const response = await apiClient.put(
      `/vacation-settings/${userId}/work-schedule-overrides`,
      overridePayload,
      { signal: options.signal }
    );

    return normalizeVacationSettingsBundle(response.data);
  } catch (error) {
    if (!isRequestCanceled(error)) {
      // eslint-disable-next-line no-console
      console.error("[VacationSettingsService] ❌ setWorkScheduleOverride failed:", error);
    }
    throw error;
  }
}

/**
 * Delete an employee-specific daily schedule exception.
 *
 * The backend soft-deactivates the row so the calculation engine will ignore
 * it while still retaining lightweight audit information.
 */
export async function deleteWorkScheduleOverride(userId, overrideId, options = {}) {
  try {
    assertValidId(userId, "userId");
    assertValidId(overrideId, "overrideId");

    const response = await apiClient.delete(
      `/vacation-settings/${userId}/work-schedule-overrides/${overrideId}`,
      { signal: options.signal }
    );

    return normalizeVacationSettingsBundle(response.data);
  } catch (error) {
    if (!isRequestCanceled(error)) {
      // eslint-disable-next-line no-console
      console.error("[VacationSettingsService] ❌ deleteWorkScheduleOverride failed:", error);
    }
    throw error;
  }
}


/* -------------------------------------------------------------------------- */
/* Leave-day norm recalculation                                               */
/* -------------------------------------------------------------------------- */

/**
 * Preview annual-balance effects after changing the employee leave-day norm.
 *
 * Endpoint:
 *   GET /vacation-settings/:userId/leave-day-norm-recalculation-preview
 *
 * This helper intentionally returns the backend payload as-is.  The UI needs
 * both "before" cached summary values and freshly computed "after" values so
 * the superuser can make an explicit, auditable recalculation decision.
 */
export async function previewLeaveDayNormRecalculation(userId, year, options = {}) {
  try {
    assertValidId(userId, "userId");

    const res = await apiClient.get(
      `/vacation-settings/${userId}/leave-day-norm-recalculation-preview`,
      {
        withCredentials: true,
        signal: options.signal,
        params: {
          year,
          includePending: options.includePending ? 1 : 0,
        },
      }
    );

    return res.data || {};
  } catch (error) {
    logRealFailure("previewLeaveDayNormRecalculation", error);
    throw error;
  }
}

/**
 * Explicitly rebuild annual summaries/request balance allocations with the
 * current user_leave_profiles.leave_day_minutes value.
 *
 * This must remain a conscious administrative action.  Saving the profile alone
 * should never silently rewrite previously generated annual summaries.
 */
export async function recalculateLeaveBalancesAfterDayNormChange(userId, payload, options = {}) {
  try {
    assertValidId(userId, "userId");

    const res = await apiClient.post(
      `/vacation-settings/${userId}/recalculate-leave-balances`,
      payload || {},
      {
        withCredentials: true,
        signal: options.signal,
      }
    );

    return res.data || {};
  } catch (error) {
    logRealFailure("recalculateLeaveBalancesAfterDayNormChange", error);
    throw error;
  }
}
